import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ASSET } from "../lib/api";
import { X, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

/**
 * Click-to-zoom product image with fullscreen modal.
 *
 * Default: shows the image large (aspect-[3/5] or given by parent), fits contain.
 * On click → opens a fullscreen modal with:
 *  - zoom in / zoom out buttons (1x → 4x)
 *  - wheel scroll → zoom
 *  - drag to pan when zoomed
 *  - ESC / click backdrop / close button to dismiss
 *
 * Props:
 *  - src: image path
 *  - alt
 *  - className: wrapper class (aspect controlled by parent)
 *  - imgClassName: extra class on the idle <img> (e.g. to scale up)
 */
export default function ProductZoom({ src, alt, className = "", imgClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const resolved = ASSET(src);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line
  }, [open]);

  const close = () => { setOpen(false); setScale(1); setPan({ x: 0, y: 0 }); };
  const zoomIn = () => setScale((s) => Math.min(4, +(s + 0.5).toFixed(2)));
  const zoomOut = () => setScale((s) => { const n = Math.max(1, +(s - 0.5).toFixed(2)); if (n === 1) setPan({ x: 0, y: 0 }); return n; });

  const onWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn(); else zoomOut();
  };

  const onPointerDown = (e) => {
    if (scale <= 1) return;
    dragRef.current = { startX: e.clientX - pan.x, startY: e.clientY - pan.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setPan({ x: e.clientX - dragRef.current.startX, y: e.clientY - dragRef.current.startY });
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
  };

  return (
    <>
      <button
        type="button"
        data-testid="product-zoom-trigger"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`group relative overflow-hidden bg-white cursor-zoom-in block w-full h-full ${className}`}
        aria-label="Ingrandisci immagine"
      >
        <img
          src={resolved}
          alt={alt}
          loading="lazy"
          className={`w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.04] ${imgClassName}`}
        />
        <div className="pointer-events-none absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-black/70 backdrop-blur text-white text-[10px] uppercase tracking-[0.2em]">
            <Maximize2 className="h-3 w-3" /> Zoom
          </div>
        </div>
      </button>

      {open && createPortal(
        <div
          data-testid="product-zoom-modal"
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
          onClick={close}
        >
          {/* Toolbar */}
          <div className="flex items-center justify-between p-4 md:p-6 text-white" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs uppercase tracking-[0.3em] text-[#C9A961]">{alt}</div>
            <div className="flex items-center gap-2">
              <button data-testid="zoom-out-btn" onClick={zoomOut} disabled={scale <= 1}
                className="h-10 w-10 inline-flex items-center justify-center border border-white/20 hover:bg-white hover:text-black disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white transition-colors">
                <ZoomOut className="h-4 w-4" />
              </button>
              <div className="min-w-[60px] text-center text-sm font-mono text-[#C9A961]">{Math.round(scale * 100)}%</div>
              <button data-testid="zoom-in-btn" onClick={zoomIn} disabled={scale >= 4}
                className="h-10 w-10 inline-flex items-center justify-center border border-white/20 hover:bg-white hover:text-black disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white transition-colors">
                <ZoomIn className="h-4 w-4" />
              </button>
              <button data-testid="zoom-close-btn" onClick={close}
                className="h-10 w-10 inline-flex items-center justify-center border border-white/20 hover:bg-white hover:text-black ml-2 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Canvas */}
          <div
            className="flex-1 overflow-hidden flex items-center justify-center bg-white"
            onWheel={onWheel}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              data-testid="zoom-modal-image"
              src={resolved}
              alt={alt}
              draggable={false}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
                cursor: scale > 1 ? "grab" : "zoom-in",
                transition: dragRef.current ? "none" : "transform 180ms ease-out",
              }}
              onDoubleClick={scale > 1 ? () => { setScale(1); setPan({ x: 0, y: 0 }); } : zoomIn}
              className="max-h-full max-w-full object-contain select-none"
            />
          </div>

          {/* Hint */}
          <div className="p-3 text-center text-[10px] uppercase tracking-[0.3em] text-white/50" onClick={(e) => e.stopPropagation()}>
            Scroll · pulsanti · doppio-click · ESC
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
